import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, type StaffOrg } from "../lib/api.ts";
import { useStaff } from "../lib/staff.ts";
import { useSortableTable, SortHeader } from "../components/SortableTable.tsx";

export function StaffOrgsPage() {
  const qc = useQueryClient();
  const { role } = useStaff();
  const isAdmin = role === "admin";
  const orgs = useQuery({
    queryKey: ["staff", "orgs"],
    queryFn: api.staffListOrgs,
  });

  const [confirmDelete, setConfirmDelete] = useState<StaffOrg | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [renaming, setRenaming] = useState<StaffOrg | null>(null);

  const block = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string | null }) =>
      api.staffBlockOrg(id, reason),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["staff", "orgs"] }),
  });
  const unblock = useMutation({
    mutationFn: (id: string) => api.staffUnblockOrg(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["staff", "orgs"] }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.staffDeleteOrg(id),
    onSuccess: () => {
      setConfirmDelete(null);
      qc.invalidateQueries({ queryKey: ["staff", "orgs"] });
    },
  });

  const create = useMutation({
    mutationFn: ({ name, slug }: { name: string; slug: string | null }) =>
      api.staffCreateOrg(name, slug),
    onSuccess: () => {
      setShowCreate(false);
      qc.invalidateQueries({ queryKey: ["staff", "orgs"] });
    },
  });
  const rename = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      api.staffRenameOrg(id, name),
    onSuccess: () => {
      setRenaming(null);
      qc.invalidateQueries({ queryKey: ["staff", "orgs"] });
    },
  });

  const sortable = useSortableTable(orgs.data?.orgs ?? [], {
    name:        (o) => o.name,
    slug:        (o) => o.slug,
    deviceCount: (o) => o.deviceCount,
    testCount:   (o) => o.testCount,
    createdAt:   (o) => o.createdAt,
    blocked:     (o) => (o.blockedAt ? 1 : 0),
  }, "createdAt", "desc");

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <h1>Organisations</h1>
        {isAdmin && (
          <button onClick={() => setShowCreate(true)}>Create org</button>
        )}
      </div>
      <p className="muted">All organisations registered on this deployment.</p>
      {orgs.isLoading && <p className="muted">Loading…</p>}
      {orgs.error && (
        <p className="err">Couldn't load orgs — {(orgs.error as Error).message}</p>
      )}
      {orgs.data && orgs.data.orgs.length === 0 && (
        <p className="muted">No organisations yet.</p>
      )}
      {orgs.data && orgs.data.orgs.length > 0 && (
        <table>
          <thead>
            <tr>
              <SortHeader label="Name"    columnKey="name"        sortKey={sortable.sortKey} sortDir={sortable.sortDir} onSort={sortable.handleSort} />
              <SortHeader label="Slug"    columnKey="slug"        sortKey={sortable.sortKey} sortDir={sortable.sortDir} onSort={sortable.handleSort} />
              <SortHeader label="Devices" columnKey="deviceCount" sortKey={sortable.sortKey} sortDir={sortable.sortDir} onSort={sortable.handleSort} />
              <SortHeader label="Tests"   columnKey="testCount"   sortKey={sortable.sortKey} sortDir={sortable.sortDir} onSort={sortable.handleSort} />
              <SortHeader label="Created" columnKey="createdAt"   sortKey={sortable.sortKey} sortDir={sortable.sortDir} onSort={sortable.handleSort} />
              <SortHeader label="Status"  columnKey="blocked"     sortKey={sortable.sortKey} sortDir={sortable.sortDir} onSort={sortable.handleSort} />
              {isAdmin && <th></th>}
            </tr>
          </thead>
          <tbody>
            {sortable.sorted.map((o) => (
              <tr key={o.id} style={o.blockedAt ? { opacity: 0.6 } : undefined}>
                <td><Link to={`/staff/orgs/${o.id}`}>{o.name}</Link></td>
                <td><code className="muted">{o.slug ?? "—"}</code></td>
                <td>{o.deviceCount}</td>
                <td>{o.testCount}</td>
                <td>{new Date(o.createdAt).toLocaleDateString()}</td>
                <td>
                  {o.blockedAt
                    ? <span className="pill fail" title={o.blockedReason ?? undefined}>Blocked</span>
                    : <span className="muted">Active</span>}
                </td>
                {isAdmin && (
                  <td style={{ whiteSpace: "nowrap" }}>
                    <button
                      className="secondary"
                      onClick={() => setRenaming(o)}
                      disabled={rename.isPending}
                    >
                      Rename
                    </button>
                    {" "}
                    {!o.blockedAt && (
                      <button
                        className="secondary"
                        onClick={() => {
                          const reason = window.prompt(`Block "${o.name}"? Optional reason:`);
                          if (reason !== null) {
                            block.mutate({ id: o.id, reason: reason.trim() || null });
                          }
                        }}
                        disabled={block.isPending}
                      >
                        Block
                      </button>
                    )}
                    {o.blockedAt && (
                      <>
                        <button
                          className="secondary"
                          onClick={() => unblock.mutate(o.id)}
                          disabled={unblock.isPending}
                        >
                          Unblock
                        </button>
                        {" "}
                        <button
                          className="danger"
                          onClick={() => setConfirmDelete(o)}
                          disabled={remove.isPending}
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {(block.error || unblock.error || remove.error || rename.error) && (
        <p className="err" style={{ marginTop: "1rem" }}>
          {((block.error || unblock.error || remove.error || rename.error) as Error).message}
        </p>
      )}

      {confirmDelete && (
        <DeleteOrgConfirm
          org={confirmDelete}
          pending={remove.isPending}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() => remove.mutate(confirmDelete.id)}
        />
      )}

      {showCreate && (
        <CreateOrgModal
          pending={create.isPending}
          error={(create.error as Error | null)?.message ?? null}
          onCancel={() => { setShowCreate(false); create.reset(); }}
          onSubmit={(name, slug) => create.mutate({ name, slug })}
        />
      )}

      {renaming && (
        <RenameOrgModal
          org={renaming}
          pending={rename.isPending}
          error={(rename.error as Error | null)?.message ?? null}
          onCancel={() => { setRenaming(null); rename.reset(); }}
          onSubmit={(name) => rename.mutate({ id: renaming.id, name })}
        />
      )}
    </>
  );
}

function RenameOrgModal({
  org, pending, error, onCancel, onSubmit,
}: {
  org: StaffOrg;
  pending: boolean;
  error: string | null;
  onCancel: () => void;
  onSubmit: (name: string) => void;
}) {
  const [name, setName] = useState(org.name);
  const trimmed = name.trim();
  const canSubmit = trimmed.length > 0 && trimmed !== org.name;
  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed", inset: 0,
        background: "rgba(15,23,42,0.5)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "1rem",
      }}
    >
      <div className="card" style={{ maxWidth: 480, width: "100%" }}>
        <h2 style={{ marginTop: 0 }}>Rename organisation</h2>
        <p className="muted" style={{ marginTop: 0 }}>
          Slug stays the same — only the display name changes.
        </p>
        <input
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          aria-label="New name"
        />
        {error && <p className="err">{error}</p>}
        <div style={{ marginTop: "1rem", display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
          <button className="secondary" onClick={onCancel} disabled={pending}>Cancel</button>
          <button disabled={!canSubmit || pending} onClick={() => onSubmit(trimmed)}>
            {pending ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CreateOrgModal({
  pending, error, onCancel, onSubmit,
}: {
  pending: boolean;
  error: string | null;
  onCancel: () => void;
  onSubmit: (name: string, slug: string | null) => void;
}) {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const canSubmit = name.trim().length > 0;
  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed", inset: 0,
        background: "rgba(15,23,42,0.5)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "1rem",
      }}
    >
      <div className="card" style={{ maxWidth: 480, width: "100%" }}>
        <h2 style={{ marginTop: 0 }}>Create organisation</h2>
        <label style={{ display: "block", marginBottom: "0.75rem" }}>
          <span style={{ display: "block", marginBottom: "0.25rem" }}>Name</span>
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Acme Fire Services"
          />
        </label>
        <label style={{ display: "block", marginBottom: "0.75rem" }}>
          <span style={{ display: "block", marginBottom: "0.25rem" }}>
            Slug <span className="muted">(optional)</span>
          </span>
          <input
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder="acme-fire (auto-generated if blank)"
          />
        </label>
        {error && <p className="err">{error}</p>}
        <div style={{ marginTop: "1rem", display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
          <button className="secondary" onClick={onCancel} disabled={pending}>Cancel</button>
          <button
            disabled={!canSubmit || pending}
            onClick={() => onSubmit(name.trim(), slug.trim() || null)}
          >
            {pending ? "Creating…" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}

function DeleteOrgConfirm({
  org, pending, onCancel, onConfirm,
}: {
  org: StaffOrg;
  pending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const [typed, setTyped] = useState("");
  const matches = typed === org.name;
  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed", inset: 0,
        background: "rgba(15,23,42,0.5)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "1rem",
      }}
    >
      <div className="card" style={{ maxWidth: 480, width: "100%" }}>
        <h2 style={{ marginTop: 0 }}>Delete organisation</h2>
        <p>
          This will permanently remove <strong>{org.name}</strong> and all of
          its data: <strong>{org.testCount}</strong> tests,{" "}
          <strong>{org.deviceCount}</strong> devices, members, invitations,
          pair codes, and every R2 blob under <code>tenants/{org.id}/</code>.
        </p>
        <p>
          This cannot be undone. Type the org name to confirm:
        </p>
        <input
          autoFocus
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder={org.name}
          aria-label="Confirm organisation name"
        />
        <div style={{ marginTop: "1rem", display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
          <button className="secondary" onClick={onCancel} disabled={pending}>
            Cancel
          </button>
          <button
            className="danger"
            disabled={!matches || pending}
            onClick={onConfirm}
          >
            {pending ? "Deleting…" : "Delete permanently"}
          </button>
        </div>
      </div>
    </div>
  );
}
